<template>
	<div
		ref="container"
		class="omapi-filter-dropdown"
		:class="{ 'omapi-filter-dropdown--open': isOpen, 'omapi-filter-dropdown--disabled': disabled }"
	>
		<button type="button" class="omapi-filter-dropdown__trigger" :disabled="disabled" @click.prevent="toggle">
			<span class="omapi-filter-dropdown__label">{{ displayLabel }}</span>
			<svg
				class="omapi-filter-dropdown__chevron"
				width="10"
				height="6"
				viewBox="0 0 10 6"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M1 1L5 5L9 1"
					stroke="currentColor"
					stroke-width="1.5"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
		</button>
		<div v-show="isOpen" class="omapi-filter-dropdown__panel">
			<ul class="omapi-filter-dropdown__list">
				<li v-if="showAll" class="omapi-filter-dropdown__item">
					<label v-if="multiple" class="omapi-filter-dropdown__option">
						<input
							type="checkbox"
							class="omapi-filter-dropdown__checkbox"
							:checked="isAllSelected"
							@change="onSelectAll"
						/>
						<span>{{ allLabel }}</span>
					</label>
					<button
						v-else
						type="button"
						class="omapi-filter-dropdown__option"
						:class="{ 'is-selected': !currentSingle }"
						@click.prevent="selectSingle('')"
					>
						{{ allLabel }}
					</button>
				</li>
				<li v-for="option in options" :key="option.value" class="omapi-filter-dropdown__item">
					<label v-if="multiple" class="omapi-filter-dropdown__option">
						<input
							type="checkbox"
							class="omapi-filter-dropdown__checkbox"
							:value="option.value"
							:checked="currentMulti.includes(option.value)"
							@change="onToggleOption(option.value)"
						/>
						<span>{{ option.label }}</span>
					</label>
					<button
						v-else
						type="button"
						class="omapi-filter-dropdown__option"
						:class="{ 'is-selected': option.value === currentSingle }"
						@click.prevent="selectSingle(option.value)"
					>
						{{ option.label }}
					</button>
				</li>
			</ul>
		</div>
	</div>
</template>

<script>
export default {
	model: {
		prop: 'value',
		event: 'input',
	},
	props: {
		/**
		 * Current value. Array for multiple, string for single.
		 */
		value: {
			type: [String, Array],
			default: () => [],
		},
		/**
		 * Selectable options: [{ value, label }]
		 */
		options: {
			type: Array,
			default: () => [],
		},
		/**
		 * Label for the "all / none selected" item.
		 */
		allLabel: {
			type: String,
			default: 'All',
		},
		/**
		 * Whether to show the "all" item at the top of the list.
		 */
		showAll: {
			type: Boolean,
			default: true,
		},
		/**
		 * Multi-select mode.
		 */
		multiple: {
			type: Boolean,
			default: false,
		},
		/**
		 * Disable the trigger button.
		 */
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			isOpen: false,
		};
	},
	computed: {
		currentMulti() {
			return Array.isArray(this.value) ? this.value : [];
		},
		currentSingle() {
			return Array.isArray(this.value) ? '' : this.value;
		},
		isAllSelected() {
			return 0 === this.currentMulti.length;
		},
		displayLabel() {
			if (this.multiple) {
				if (0 === this.currentMulti.length) {
					return this.allLabel;
				}
				if (1 === this.currentMulti.length) {
					const opt = this.options.find((o) => o.value === this.currentMulti[0]);
					return opt ? opt.label : this.allLabel;
				}
				return `${this.currentMulti.length} selected`;
			}
			if (!this.currentSingle) {
				return this.allLabel;
			}
			const opt = this.options.find((o) => o.value === this.currentSingle);
			return opt ? opt.label : this.allLabel;
		},
	},
	mounted() {
		document.addEventListener('click', this.onOutsideClick);
	},
	beforeDestroy() {
		document.removeEventListener('click', this.onOutsideClick);
	},
	methods: {
		toggle() {
			if (!this.disabled) {
				this.isOpen = !this.isOpen;
			}
		},
		close() {
			this.isOpen = false;
		},
		onOutsideClick(e) {
			if (this.isOpen && this.$refs.container && !this.$refs.container.contains(e.target)) {
				this.close();
			}
		},
		/**
		 * Selecting "All" clears all specific selections.
		 *
		 * @returns {void}
		 */
		onSelectAll() {
			this.$emit('input', []);
		},
		/**
		 * Toggle a single option in multiselect.
		 *
		 * @param {string} val The option value to toggle.
		 *
		 * @returns {void}
		 */
		onToggleOption(val) {
			const current = [...this.currentMulti];
			const idx = current.indexOf(val);
			if (-1 === idx) {
				current.push(val);
			} else {
				current.splice(idx, 1);
			}
			this.$emit('input', current);
		},
		/**
		 * Select a value in single-select mode and close.
		 *
		 * @param {string} val The option value to select.
		 *
		 * @returns {void}
		 */
		selectSingle(val) {
			this.$emit('input', val);
			this.close();
		},
	},
};
</script>

<style lang="scss">
@import '../../../assets/scss/variables';

.omapi-filter-dropdown {
	position: relative;
	display: inline-block;

	&__trigger {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		height: 30px;
		padding: 0 8px 0 10px;
		background: $color_white;
		border: 1px solid $color_grey-border;
		border-radius: 3px;
		cursor: pointer;
		font-size: 13px;
		color: $font_dark-color;
		font-weight: $font_normal;
		gap: 6px;
		white-space: nowrap;

		&:hover:not(:disabled) {
			border-color: darken($color_grey-border, 15%);
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}
	}

	&--open &__trigger {
		border-color: $color_blue-primary;
		outline: none;
	}

	&__label {
		overflow: hidden;
		text-overflow: ellipsis;
		min-width: 0;
	}

	&__chevron {
		flex-shrink: 0;
		transition: transform 0.15s ease;
		color: $font_secondary-color;
	}

	&--open &__chevron {
		transform: rotate(180deg);
	}

	&__panel {
		position: absolute;
		top: calc(100% + 3px);
		left: 0;
		z-index: 9999;
		background: $color_white;
		border: 1px solid $color_grey-border;
		border-radius: 3px;
		box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
		min-width: 100%;
		max-height: 240px;
		overflow-y: auto;
	}

	&__list {
		list-style: none;
		margin: 0;
		padding: 4px 0;
	}

	&__item {
		margin: 0;
	}

	&__option {
		display: flex;
		align-items: center;
		gap: 7px;
		padding: 6px 12px;
		font-size: 13px;
		color: $font_dark-color;
		cursor: pointer;
		text-decoration: none;
		white-space: nowrap;
		font-weight: $font_normal;
		width: 100%;
		background: none;
		border: none;
		text-align: left;
		line-height: 1.4;

		&:hover {
			background-color: $color_blue-background;
			color: $color_blue-primary;
			text-decoration: none;
		}

		&.is-selected {
			color: $color_blue-primary;
			font-weight: $font_bold;
		}
	}

	&__checkbox {
		margin: 0;
		flex-shrink: 0;
		cursor: pointer;
	}
}
</style>
